import { useParams, Link } from 'react-router-dom';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import type { Movie } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id || '1');

  if (!movie) {
    return (
      <div className="pt-20">
        <div className="px-4 md:px-6">
          <div className="max-w-[1200px] mx-auto text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-error-warning-line text-2xl" />
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">未找到内容</h1>
            <p className="text-foreground-500 mb-6">该影片不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer no-underline"
            >
              <i className="ri-home-line mr-2" /> 返回首页
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const relatedMovies = getRelatedMovies(movie);

  const categoryMap: Record<string, string> = {
    movie: '电影',
    tv: '电视剧',
    variety: '综艺',
    anime: '动漫',
    documentary: '纪录片',
  };

  const categoryName = categoryMap[movie.category] || movie.category;

  return (
    <>
      <div className="pt-20">
        <div className="px-4 md:px-6">
          <div className="max-w-[1200px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName, href: `/category/${movie.category}` },
                { label: movie.title },
              ]}
            />
          </div>
        </div>

        <main>
          <article className="px-4 md:px-6 py-6">
            <div className="max-w-[1200px] mx-auto">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-shrink-0 w-full lg:w-[320px]">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100 sticky top-24">
                    <img
                      src={movie.posterImage}
                      alt={`${movie.title} - 蚂蚁影视高清免费在线观看`}
                      title={`${movie.title} - 蚂蚁影视高清免费在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-start gap-3 mb-3">
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                      {movie.title}
                    </h1>
                    {movie.tags.map((tag) => (
                      <span
                        key={tag}
                        className={`px-2 py-0.5 text-xs font-medium rounded whitespace-nowrap mt-1 ${
                          tag === '热播'
                            ? 'bg-primary-100 text-primary-700'
                            : tag === '新片' || tag === '新剧' || tag === '新番' || tag === '新季'
                            ? 'bg-accent-100 text-accent-700'
                            : 'bg-background-200 text-foreground-600'
                        }`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <p className="text-sm text-foreground-500 mb-6">{movie.subtitle}</p>

                  <div className="flex items-center gap-6 mb-6 flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <span className="text-2xl font-bold text-accent-600">{movie.rating}</span>
                      <span className="text-xs text-foreground-400">/ 10</span>
                    </div>
                    <span className="text-sm text-foreground-600">{movie.year}</span>
                    <span className="text-sm text-foreground-600">{movie.duration}</span>
                    <span className="text-sm text-foreground-600">{movie.region}</span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {movie.genres.map((genre) => (
                      <span
                        key={genre}
                        className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-200/70 whitespace-nowrap"
                      >
                        {genre}
                      </span>
                    ))}
                  </div>

                  <div className="space-y-4 mb-8">
                    <div className="flex gap-3">
                      <span className="text-sm text-foreground-400 whitespace-nowrap min-w-[4em]">导演：</span>
                      <span className="text-sm text-foreground-800">{movie.director}</span>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-sm text-foreground-400 whitespace-nowrap min-w-[4em]">主演：</span>
                      <span className="text-sm text-foreground-800">{movie.actors.join(' / ')}</span>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-sm text-foreground-400 whitespace-nowrap min-w-[4em]">地区：</span>
                      <span className="text-sm text-foreground-800">{movie.region}</span>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-sm text-foreground-400 whitespace-nowrap min-w-[4em]">更新：</span>
                      <span className="text-sm text-foreground-800">{movie.updateDate}</span>
                    </div>
                  </div>

                  <div className="mb-8">
                    <button className="inline-flex items-center px-8 py-3 bg-primary-500 text-background-50 rounded-full text-base font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
                      <i className="ri-play-circle-fill text-xl mr-2" />
                      立即播放
                    </button>
                  </div>

                  <div className="border-t border-background-200/70 pt-6">
                    <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">剧情简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {movie.description}
                    </p>
                  </div>
                </div>
              </div>

              {relatedMovies.length > 0 && (
                <section className="mt-12 pt-8 border-t border-background-200/70">
                  <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
                    {relatedMovies.map((related: Movie) => (
                      <MovieCard key={related.id} movie={related} size="small" />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </article>
        </main>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: movie.title,
            description: movie.description,
            image: movie.posterImage,
            dateCreated: `${movie.year}`,
            director: {
              '@type': 'Person',
              name: movie.director,
            },
            actor: movie.actors.map((a) => ({
              '@type': 'Person',
              name: a,
            })),
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: movie.rating,
              bestRating: '10',
              worstRating: '0',
            },
          }),
        }}
      />
    </>
  );
}